import { Component, Element, h, Host, Prop, State, Watch } from '@stencil/core';
import { translations } from './translations';
import { en, awaitTopbar, uuidv4 } from '@/globals/helpers';
import type { PnLanguages, PnStepProps } from '@/globals/types';

/**
 * The `pn-progress-stepper` is used to visualize the users progress through a flow (checkout/survey, etc...).
 **/
@Component({
  tag: 'pn-progress-stepper',
  styleUrl: 'pn-progress-stepper.scss',
})
export class PnProgressStepper {
  private readonly id: string = `pn-progress-stepper-${uuidv4()}`;

  private readonly minStep: 1 = 1;
  private readonly maxStep: 7 = 7;

  @Element() hostElement: HTMLElement;

  @State() private builtInLabel: string;
  /** Percentage calculated with currentStep / totalSteps. */
  @State() private progressValue: number = 0;

  /** Set custom label for the stepper, default label is "Step". */
  @Prop() label?: string = '';
  /** Set a custom HTML id on the progress stepper element to associate progress bar with label. */
  @Prop() progressStepperId?: string = '';
  /**
   * Set the language manually, only use this prop if the pnTopbar is not loaded.
   * Will not overwrite the custom `label` prop if used.
   **/
  @Prop() language?: PnLanguages = null;

  /** Set total amount of steps, maximum allowed is `7`. @category Steps */
  @Prop({ mutable: true }) totalSteps: number = this.maxStep;
  /** The current step of the progress stepper. @category Steps */
  @Prop({ mutable: true }) currentStep: number = this.minStep;
  /** You can add a custom optional string to the label if you need a name for the current step. @category Steps */
  @Prop() stepName?: string;

  /**
   * Use the dots visual, instead of the bar.
   *
   * This will hide the label from view, but is still accessible to screenreaders.
   *
   * @since v7.16.0
   * @category Visual
   */
  @Prop() dots: boolean = false;

  @Watch('currentStep')
  @Watch('totalSteps')
  stepWatchers(a: number, b: number, c: 'currentStep' | 'totalSteps') {
    if (c === 'currentStep') this.validate(a, 'currentStep');
    if (c === 'totalSteps') this.validate(b, 'totalSteps');
    this.calculateProgress();
  }

  @Watch('language')
  watchLanguage() {
    this.builtInLabel = translations.STEP[this.language || en];
  }

  async componentWillLoad() {
    this.watchLanguage();

    this.validate(this.currentStep, 'currentStep');
    this.validate(this.totalSteps, 'totalSteps');

    this.calculateProgress();

    if (this.language === null) await awaitTopbar(this.hostElement);
  }

  private validate(step: number, prop: PnStepProps) {
    const componentName = this.hostElement.localName;
    if (step < this.minStep) {
      console.warn(
        `${componentName}: The ${prop} ${step} is below the minimum allowed steps of ${this.minStep}. ${prop} will default to ${this.minStep}.`,
      );
      this[prop] = this.minStep;
    }
    if (prop === 'currentStep' && step > this.totalSteps) {
      console.warn(
        `${componentName}: The ${prop} ${step} is above totalSteps ${this.totalSteps}, ${prop} will default to ${this.totalSteps}.`,
      );
      this[prop] = this.totalSteps;
    }
    if (step > this.maxStep) {
      console.warn(
        `${componentName}: The ${prop} ${step} is above the maximum allowed steps of ${this.maxStep}. ${prop} will default to ${this.maxStep}.`,
      );
      this[prop] = this.maxStep;
    }
  }

  private calculateProgress() {
    try {
      this.progressValue = Math.floor((this.currentStep / this.totalSteps) * 100);
      this.hostElement.style.setProperty('--pn-progress-value', `${this.progressValue}%`);
    } catch (error) {
      console.error(`${this.hostElement.localName}:`, error.message);
    }
  }

  private getLabel() {
    return this.label || this.builtInLabel;
  }

  private getId() {
    return this.progressStepperId || this.id;
  }

  render() {
    return (
      <Host>
        <label htmlFor={this.getId()} class={'pn-progress-label' + (this.dots ? ' pn-progress-sr-only' : '')}>
          <span>
            {this.getLabel()} {this.currentStep}/{this.totalSteps}
          </span>
          {this.stepName && <span> - {this.stepName}</span>}
        </label>
        <progress id={this.getId()} class="pn-progress-sr-only" max="100" value={this.progressValue} />

        {this.dots ? (
          <div class="pn-progress-dots" aria-hidden="true">
            {[...Array(this.totalSteps)].map((_, i) => (
              <div class="pn-progress-dot" data-active={i + 1 <= this.currentStep} key={i} />
            ))}
          </div>
        ) : (
          <div aria-hidden="true" class="pn-progress">
            <div class="pn-progress-value" data-full={this.currentStep === this.totalSteps} />
          </div>
        )}
      </Host>
    );
  }
}
